import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateScheduledMaintenanceCache, useSkipScheduledMaintenances } from 'src/api/scheduled-maintenance'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { IScheduledMaintenance } from 'src/generated-types/scheduled-maintenance'
import { ScheduledMaintenanceFilterParams } from 'src/generated-types/scheduled-maintenance-filter'
import { ShowDialogProps } from 'src/show-dialog'
import { getTranslatedText } from 'src/utils'
import { PerformMaintenanceForm } from './PerformMaintenanceForm'

interface ScheduledMaintenanceSkipDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  scheduledMaintenance?: IScheduledMaintenance
  mainComponentType?: IMainComponentType
  isPerformAll: boolean
  onlyDaily: boolean
  cagePositionId?: EntityId
  filterIds?: ScheduledMaintenanceFilterParams
}

export const ScheduledMaintenanceSkipDialog: React.FC<ScheduledMaintenanceSkipDialogProps> = ({
  pop,
  customerId,
  localityId,
  scheduledMaintenance,
  mainComponentType,
  isPerformAll,
}) => {
  const { t } = useTranslation(['common-api-message', 'maintenance'])

  const mutation = useSkipScheduledMaintenances(customerId, localityId)
  const onSubmit = async form => {
    form.scheduledMaintenanceIds = scheduledMaintenance?.id ? [scheduledMaintenance.id] : []
    await mutation.mutateAsync(form, {
      onSuccess: () => {
        invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(
          customerId,
          localityId,
          scheduledMaintenance?.mainComponent?.positionId as EntityId,
          scheduledMaintenance?.mainComponent?.typeId as EntityId,
        )
        invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId, localityId)
        invalidateScheduledMaintenanceCache.getMaintenanceList(customerId, localityId)
        invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
        invalidateMainComponentCache.getMaintenancePerformHistory(customerId)
        invalidateScheduledMaintenanceCache.getEquipmentPerformedMaintenances(customerId, localityId)
        invalidateScheduledMaintenanceCache.getLocalityPerformedMaintenances(customerId, localityId)
        invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId)
        invalidationActivityLogCache.getCageActivityLogs()
        invalidationActivityLogCache.getCageMaintenanceLogs()
        toast.success(t('skipped-successfully', { ns: 'maintenance' }))
        pop()
      },
      onError: () => {
        toast.error(t('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  return (
    <PerformMaintenanceForm
      onSubmit={onSubmit}
      onCancel={pop}
      maintenanceName={
        mainComponentType
          ? getTranslatedText(mainComponentType.name)
          : (scheduledMaintenance?.maintenance?.name as string)
      }
      note={mainComponentType ? '' : scheduledMaintenance?.maintenance?.note}
      mainComponentSerialNumbers={[]}
      isPerformAll={isPerformAll}
      file={scheduledMaintenance?.maintenance?.file}
      isSkip={true}
      isSubmitting={mutation.isLoading}
    />
  )
}
